import { D, Decimal, ONE, ZERO, formatNumber, roundTo, type DecimalInput } from '../decimal.js';
import {
  DIMENSIONLESS,
  defaultUnits,
  dimDiv,
  dimEquals,
  dimMul,
  dimPow,
  formatDimension,
  isDimensionless,
  type Dimension,
  type ResolvedUnit,
  type UnitRegistry,
} from '../units.js';
import { collectIdentifiers, type Node } from './ast.js';
import { FormulaError, MISSING_DATA_MESSAGE } from './errors.js';
import { parseFormula } from './parser.js';

/** Valeur d'entrée d'une formule : nombre + unité + provenance (pour la traçabilité). */
export interface Quantity {
  value: DecimalInput | null | undefined;
  unit?: string;
  /** Provenance lisible : « type S1 », « niveau R+1 », « saisie manuelle »… */
  source?: string;
  label?: string;
}
export type ScopeValue = Quantity | readonly Quantity[];
export type Scope = Readonly<Record<string, ScopeValue>>;

export interface EvaluateOptions {
  units?: UnitRegistry;
  /** Unité attendue du résultat (« m3 »). Le résultat est converti dans cette unité. */
  outputUnit?: string;
  /** Contrôle dimensionnel strict (par défaut : oui). */
  strictDimensions?: boolean;
}

export interface TraceInput {
  value: string;
  unit?: string;
  source?: string;
  label?: string;
}

export interface FormulaResult {
  value: Decimal;
  unit?: string;
  unitSymbol: string;
  dimension: Dimension;
  /** Formule normalisée, avec les noms : « L × b × h ». */
  formula: string;
  /** Formule avec valeurs substituées : « 35 × 0,25 × 0,50 ». */
  substituted: string;
  /** Résultat affichable : « 4,375 m³ ». */
  display: string;
  /** Étapes « Voir le calcul ». */
  steps: string[];
  inputs: Record<string, TraceInput>;
}

interface Val {
  v: Decimal;
  d: Dimension;
}

function isList(value: ScopeValue | undefined): value is readonly Quantity[] {
  return Array.isArray(value);
}

class Evaluator {
  readonly inputs: Record<string, TraceInput> = {};

  constructor(
    private readonly scope: Scope,
    private readonly units: UnitRegistry,
    private readonly strict: boolean,
  ) {}

  private unitOf(code: string | undefined, pos?: number): ResolvedUnit | undefined {
    if (!code) return undefined;
    try {
      return this.units.resolve(code);
    } catch {
      throw new FormulaError('UNIT', `Unité inconnue « ${code} »`, pos);
    }
  }

  private lookup(name: string, pos: number): ScopeValue {
    const value = this.scope[name];
    if (value === undefined) {
      throw new FormulaError('UNKNOWN_IDENTIFIER', `Paramètre inconnu « ${name} »`, pos, { identifier: name });
    }
    return value;
  }

  private quantity(q: Quantity, name: string, pos: number): Val {
    if (q.value === null || q.value === undefined || q.value === '') {
      throw new FormulaError('MISSING_DATA', `${q.label ?? name} : ${MISSING_DATA_MESSAGE}`, pos, { identifier: name });
    }
    const unit = this.unitOf(q.unit, pos);
    const raw = D(q.value);
    return { v: unit ? raw.mul(unit.factor) : raw, d: unit ? unit.dimension : DIMENSIONLESS };
  }

  private record(name: string, q: Quantity): void {
    if (this.inputs[name]) return;
    // La saisie d'origine est conservée (« 1.20 » reste « 1.20 ») pour un affichage fidèle.
    const entry: TraceInput = { value: q.value === null || q.value === undefined ? '' : typeof q.value === 'string' ? D(q.value).toFixed(Math.max(0, (q.value.trim().split(/[.,]/)[1] ?? '').length)) : D(q.value).toString() };
    if (q.unit) entry.unit = q.unit;
    if (q.source) entry.source = q.source;
    if (q.label) entry.label = q.label;
    this.inputs[name] = entry;
  }

  private sameDim(a: Dimension, b: Dimension, what: string, pos: number): void {
    if (this.strict && !dimEquals(a, b)) {
      throw new FormulaError(
        'DIMENSION',
        `${what} : grandeurs incompatibles (${formatDimension(a)} et ${formatDimension(b)})`,
        pos,
      );
    }
  }

  /** Analyse dimensionnelle statique (ne nécessite pas les valeurs). */
  dimOf(node: Node): Dimension {
    switch (node.kind) {
      case 'num':
        return this.unitOf(node.unit, node.pos)?.dimension ?? DIMENSIONLESS;
      case 'ident': {
        const s = this.lookup(node.name, node.pos);
        if (isList(s)) throw new FormulaError('SYNTAX', `« ${node.name} » est une liste : utilisez sum() ou count()`, node.pos);
        return this.unitOf(s.unit, node.pos)?.dimension ?? DIMENSIONLESS;
      }
      case 'unary':
        return this.dimOf(node.arg);
      case 'compare':
        this.sameDim(this.dimOf(node.left), this.dimOf(node.right), 'Comparaison', node.pos);
        return DIMENSIONLESS;
      case 'binary': {
        const l = this.dimOf(node.left);
        if (node.op === '^') {
          if (!isDimensionless(this.dimOf(node.right))) throw new FormulaError('DIMENSION', 'Un exposant doit être sans dimension', node.pos);
          if (isDimensionless(l)) return l;
          if (node.right.kind !== 'num' || !node.right.value.isInteger()) {
            throw new FormulaError('DIMENSION', 'Une grandeur dimensionnée ne peut être élevée qu\'à une puissance entière littérale', node.pos);
          }
          return dimPow(l, node.right.value.toNumber());
        }
        const r = this.dimOf(node.right);
        if (node.op === '*') return dimMul(l, r);
        if (node.op === '/') return dimDiv(l, r);
        this.sameDim(l, r, node.op === '+' ? 'Addition' : 'Soustraction', node.pos);
        return l;
      }
      case 'call':
        return this.dimOfCall(node);
    }
  }

  private dimOfCall(node: Extract<Node, { kind: 'call' }>): Dimension {
    const [a, b, c] = node.args;
    switch (node.name) {
      case 'pi':
      case 'count':
        return DIMENSIONLESS;
      case 'sum': {
        if (a?.kind !== 'ident') throw new FormulaError('SYNTAX', 'sum() attend le nom d\'une liste', node.pos);
        const list = this.lookup(a.name, a.pos);
        const items = isList(list) ? list : [list];
        const dims = items.map((q) => this.unitOf(q.unit, a.pos)?.dimension ?? DIMENSIONLESS);
        const first = dims[0] ?? DIMENSIONLESS;
        dims.forEach((d) => this.sameDim(first, d, 'sum()', node.pos));
        return first;
      }
      case 'sqrt': {
        const d = this.dimOf(a!);
        if ([d.L, d.M, d.T, d.CUR].some((e) => e % 2 !== 0)) throw new FormulaError('DIMENSION', 'Racine carrée d\'une grandeur à exposant impair', node.pos);
        return dimPow(d, 0.5);
      }
      case 'if': {
        this.dimOf(a!);
        const db = this.dimOf(b!);
        this.sameDim(db, this.dimOf(c!), 'if()', node.pos);
        return db;
      }
      case 'round':
        if (b && !isDimensionless(this.dimOf(b))) throw new FormulaError('DIMENSION', 'Le nombre de décimales doit être sans dimension', node.pos);
        return this.dimOf(a!);
      case 'min':
      case 'max': {
        const first = this.dimOf(a!);
        node.args.slice(1).forEach((arg) => this.sameDim(first, this.dimOf(arg), `${node.name}()`, node.pos));
        return first;
      }
      default:
        return this.dimOf(a!);
    }
  }

  eval(node: Node): Val {
    switch (node.kind) {
      case 'num': {
        const unit = this.unitOf(node.unit, node.pos);
        return { v: unit ? node.value.mul(unit.factor) : node.value, d: unit?.dimension ?? DIMENSIONLESS };
      }
      case 'ident': {
        const s = this.lookup(node.name, node.pos);
        if (isList(s)) throw new FormulaError('SYNTAX', `« ${node.name} » est une liste : utilisez sum() ou count()`, node.pos);
        const val = this.quantity(s, node.name, node.pos);
        this.record(node.name, s);
        return val;
      }
      case 'unary': {
        const a = this.eval(node.arg);
        return { v: a.v.neg(), d: a.d };
      }
      case 'compare': {
        const l = this.eval(node.left);
        const r = this.eval(node.right);
        this.sameDim(l.d, r.d, 'Comparaison', node.pos);
        const c = l.v.comparedTo(r.v);
        const ok = { '<': c < 0, '<=': c <= 0, '>': c > 0, '>=': c >= 0, '==': c === 0, '!=': c !== 0 }[node.op];
        return { v: ok ? ONE : ZERO, d: DIMENSIONLESS };
      }
      case 'binary':
        return this.evalBinary(node);
      case 'call':
        return this.evalCall(node);
    }
  }

  private evalBinary(node: Extract<Node, { kind: 'binary' }>): Val {
    const l = this.eval(node.left);
    const r = this.eval(node.right);
    switch (node.op) {
      case '+':
        this.sameDim(l.d, r.d, 'Addition', node.pos);
        return { v: l.v.plus(r.v), d: l.d };
      case '-':
        this.sameDim(l.d, r.d, 'Soustraction', node.pos);
        return { v: l.v.minus(r.v), d: l.d };
      case '*':
        return { v: l.v.mul(r.v), d: dimMul(l.d, r.d) };
      case '/':
        if (r.v.isZero()) throw new FormulaError('DIVISION_BY_ZERO', 'Division par zéro', node.pos);
        return { v: l.v.div(r.v), d: dimDiv(l.d, r.d) };
      case '^': {
        const d = this.dimOf(node);
        if (l.v.isNegative() && !r.v.isInteger()) throw new FormulaError('DOMAIN', "Puissance non entière d'un nombre négatif", node.pos);
        if (l.v.isZero() && r.v.isNegative()) throw new FormulaError('DIVISION_BY_ZERO', 'Division par zéro', node.pos);
        return { v: l.v.pow(r.v), d };
      }
    }
  }

  private evalCall(node: Extract<Node, { kind: 'call' }>): Val {
    const [a, b, c] = node.args;
    switch (node.name) {
      case 'pi':
        return { v: Decimal.acos(-1), d: DIMENSIONLESS };
      case 'sum':
      case 'count': {
        if (a?.kind !== 'ident') throw new FormulaError('SYNTAX', `${node.name}() attend le nom d'une liste`, node.pos);
        const list = this.lookup(a.name, a.pos);
        const items = isList(list) ? list : [list];
        if (node.name === 'count') return { v: D(items.length), d: DIMENSIONLESS };
        const d = this.dimOfCall(node);
        let total = ZERO;
        items.forEach((q, i) => {
          total = total.plus(this.quantity(q, `${a.name}[${i + 1}]`, a.pos).v);
        });
        this.inputs[a.name] = { value: `Σ ${items.length} élément(s)` };
        return { v: total, d };
      }
      case 'if': {
        const d = this.dimOfCall(node);
        const cond = this.eval(a!);
        const chosen = this.eval(cond.v.isZero() ? c! : b!);
        return { v: chosen.v, d };
      }
      case 'min':
      case 'max': {
        const d = this.dimOfCall(node);
        const vals = node.args.map((arg) => this.eval(arg).v);
        return { v: node.name === 'min' ? Decimal.min(...vals) : Decimal.max(...vals), d };
      }
      case 'ceil':
      case 'floor':
      case 'abs': {
        const x = this.eval(a!);
        return { v: node.name === 'ceil' ? x.v.ceil() : node.name === 'floor' ? x.v.floor() : x.v.abs(), d: x.d };
      }
      case 'round': {
        const x = this.eval(a!);
        const n = b ? this.eval(b).v : ZERO;
        if (!n.isInteger() || n.isNegative()) throw new FormulaError('DOMAIN', 'round() : nombre de décimales entier positif attendu', node.pos);
        return { v: roundTo(x.v, n.toNumber()), d: x.d };
      }
      case 'sqrt': {
        const d = this.dimOfCall(node);
        const x = this.eval(a!);
        if (x.v.isNegative()) throw new FormulaError('DOMAIN', 'Racine carrée d\'un nombre négatif', node.pos);
        return { v: x.v.sqrt(), d };
      }
      default:
        throw new FormulaError('UNKNOWN_FUNCTION', `Fonction inconnue « ${node.name} »`, node.pos);
    }
  }
}

// ───────────────────────────── Rendu lisible (« Voir le calcul ») ─────────────────────────────

const PREC: Record<string, number> = { compare: 1, '+': 2, '-': 2, '*': 3, '/': 3, unary: 4, '^': 5 };
const OP_TEXT: Record<string, string> = { '+': ' + ', '-': ' − ', '*': ' × ', '/': ' ÷ ', '^': '^' };
const CMP_TEXT: Record<string, string> = { '<': ' < ', '<=': ' ≤ ', '>': ' > ', '>=': ' ≥ ', '==': ' = ', '!=': ' ≠ ' };

function prec(node: Node): number {
  if (node.kind === 'binary') return PREC[node.op]!;
  if (node.kind === 'compare') return PREC.compare!;
  if (node.kind === 'unary') return PREC.unary!;
  return 9;
}

type Leaf = (node: Extract<Node, { kind: 'ident' | 'num' }>) => string;

function render(node: Node, leaf: Leaf): string {
  const wrap = (child: Node, needParens: boolean) => (needParens ? `(${render(child, leaf)})` : render(child, leaf));
  switch (node.kind) {
    case 'num':
    case 'ident':
      return leaf(node);
    case 'unary':
      return '−' + wrap(node.arg, prec(node.arg) < PREC.unary!);
    case 'compare':
      return wrap(node.left, prec(node.left) <= PREC.compare!) + CMP_TEXT[node.op] + wrap(node.right, prec(node.right) <= PREC.compare!);
    case 'binary': {
      const p = PREC[node.op]!;
      const leftParens = node.op === '^' ? prec(node.left) <= p : prec(node.left) < p;
      const rightParens = node.op === '^' ? prec(node.right) < p : prec(node.right) < p || (prec(node.right) === p && (node.op === '-' || node.op === '/'));
      return wrap(node.left, leftParens) + OP_TEXT[node.op] + wrap(node.right, rightParens);
    }
    case 'call':
      return `${node.name}(${node.args.map((a) => render(a, leaf)).join(' ; ')})`;
  }
}

function formatValue(value: Decimal, unit: ResolvedUnit | undefined): string {
  const counting = !unit || unit.displayDecimals === 0;
  return formatNumber(value, { minDecimals: counting || value.isInteger() ? 0 : 2, maxDecimals: 6 });
}

/**
 * Évalue une formule et produit sa trace complète.
 *
 * @example
 * evaluateFormula('L * b * h', { L: { value: 35, unit: 'm' }, b: { value: 0.25, unit: 'm' }, h: { value: 0.5, unit: 'm' } }, { outputUnit: 'm3' })
 * // → value 4.375, substituted « 35 × 0,25 × 0,50 », display « 4,375 m³ »
 */
export function evaluateFormula(formula: string | Node, scope: Scope, options: EvaluateOptions = {}): FormulaResult {
  const units = options.units ?? defaultUnits;
  const strict = options.strictDimensions ?? true;
  const ast = typeof formula === 'string' ? parseFormula(formula) : formula;
  const ev = new Evaluator(scope, units, strict);
  const raw = ev.eval(ast);

  let value = raw.v;
  let unitSymbol = formatDimension(raw.d);
  let decimals = 3;
  if (options.outputUnit) {
    let out: ResolvedUnit;
    try {
      out = units.resolve(options.outputUnit);
    } catch {
      throw new FormulaError('UNIT', `Unité de résultat inconnue « ${options.outputUnit} »`);
    }
    if (strict && !dimEquals(raw.d, out.dimension)) {
      throw new FormulaError(
        'DIMENSION',
        `La formule produit une grandeur en ${formatDimension(raw.d)} alors que l'unité attendue est ${out.symbol} (${formatDimension(out.dimension)})`,
        undefined,
        { produced: raw.d, expected: out.dimension },
      );
    }
    value = raw.v.div(out.factor);
    unitSymbol = out.symbol;
    decimals = out.displayDecimals;
  } else if (isDimensionless(raw.d)) {
    unitSymbol = '';
  }

  const names = render(ast, (n) => (n.kind === 'ident' ? n.name : n.raw.replace('.', ',')));
  const substituted = render(ast, (n) => {
    if (n.kind === 'num') return n.raw.replace('.', ',');
    const q = scope[n.name];
    if (!q || isList(q) || q.value === null || q.value === undefined || q.value === '') return n.name;
    const unit = q.unit ? units.resolve(q.unit) : undefined;
    return formatValue(D(q.value as DecimalInput).mul(unit?.factor ?? ONE), unit);
  });
  const display = `${formatNumber(value, { maxDecimals: Math.max(decimals, 0) })}${unitSymbol ? ' ' + unitSymbol : ''}`;
  const steps = names === substituted ? [names, `= ${display}`] : [names, substituted, `= ${display}`];

  const result: FormulaResult = {
    value,
    unitSymbol,
    dimension: raw.d,
    formula: names,
    substituted,
    display,
    steps,
    inputs: ev.inputs,
  };
  if (options.outputUnit) result.unit = options.outputUnit;
  return result;
}

export interface FormulaAnalysis {
  ok: boolean;
  identifiers: string[];
  dimension?: Dimension;
  error?: FormulaError;
}

/**
 * Validation d'une formule sans valeurs (éditeur de familles) : syntaxe, identifiants connus,
 * cohérence dimensionnelle avec l'unité de sortie. `scope` n'a besoin que des unités.
 */
export function analyzeFormula(formula: string, scope: Scope, options: EvaluateOptions = {}): FormulaAnalysis {
  const units = options.units ?? defaultUnits;
  try {
    const ast = parseFormula(formula);
    const ids = [...collectIdentifiers(ast)];
    const ev = new Evaluator(scope, units, options.strictDimensions ?? true);
    const d = ev.dimOf(ast);
    if (options.outputUnit && (options.strictDimensions ?? true)) {
      const out = units.resolve(options.outputUnit);
      if (!dimEquals(d, out.dimension)) {
        throw new FormulaError('DIMENSION', `La formule produit ${formatDimension(d)} alors que l'unité attendue est ${out.symbol}`);
      }
    }
    return { ok: true, identifiers: ids, dimension: d };
  } catch (error) {
    if (error instanceof FormulaError) return { ok: false, identifiers: [], error };
    throw error;
  }
}

